/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Box, Typography, Grid } from "@mui/material";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { getAPICall } from "lib/callApi";
import {
  fetchDateRangeForWidget,
  formatDatesForWidget,
  handlePeriodsForWidget,
} from "utils/HelperFunctions";
import "../../styles/widget.scss";

export default function MonthlySnapshot({ selectedUser, selectedTab, widget }) {
  const { dataSource, widgetTitle, displayName } = widget;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [periodDates, setPeriodDates] = useState({});
  const [snapshotData, setSnapshotData] = useState([]);
  const [displayData, setDisplayData] = useState({});

  const timePeriod = "monthly";
  const [loading, setLoading] = useState(true);

  const calculateDisplayData = () => {
    if (snapshotData?.length) {
      let display = { underContract: 0, closings: 0, avgPricePerClosing: 0 };
      if (selectedTab) {
        if (selectedUser) {
          const user = snapshotData.find((x) => x.userId === selectedUser);
          if (user) {
            display = {
              underContract: user.totalUnderContract,
              closings: user.totalClosings,
              avgPricePerClosing: user.avgPricePerClosing,
            };
          }
        } else {
          display = snapshotData.reduce(
            (acc, curr) => ({
              underContract: acc.underContract + curr.totalUnderContract,
              closings: acc.closings + curr.totalClosings,
              avgPricePerClosing: acc.avgPricePerClosing + curr.avgPricePerClosing,
            }),
            display
          );
          display.avgPricePerClosing = (display.avgPricePerClosing / snapshotData.length).toFixed(
            1
          );
        }
      } else {
        const user = snapshotData.find((x) => x.userCompanyLinkId === loginUser.userCompanyLinkId);
        if (user) {
          display = {
            underContract: user.totalUnderContract,
            closings: user.totalClosings,
            avgPricePerClosing: user.avgPricePerClosing,
          };
        }
      }
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(display.avgPricePerClosing);
      display.avgPricePerClosing = formatted;
      setDisplayData(display);
    }
  };

  const handlePeriods = (type) => {
    if (!periodDates.start && !periodDates.end) return;
    const dates = handlePeriodsForWidget(type, timePeriod, periodDates);
    if (
      dates.start &&
      dates.end &&
      dates.start !== periodDates.start &&
      dates.end !== periodDates.end
    ) {
      setPeriodDates(dates);
    }
  };

  const fetchMonthlySnapshotData = async () => {
    if (widget.connectedStatus && dataSource && periodDates.start && periodDates.end) {
      setLoading(true);
      const result = await getAPICall(dataSource, true, {
        start: periodDates.start,
        end: periodDates.end,
      });
      if (result?.data?.success) {
        setSnapshotData(result.data.data);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    calculateDisplayData();
  }, [snapshotData, selectedTab, selectedUser]);

  useEffect(() => {
    fetchMonthlySnapshotData();
  }, [periodDates]);

  useEffect(() => {
    const dates = fetchDateRangeForWidget(timePeriod);
    setPeriodDates(dates);
  }, []);

  return (
    <Box p={2}>
      <Box
        sx={{
          pl: 1,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          "& .big-text": { fontSize: "1rem", fontWeight: "bold" },
        }}
      >
        {selectedTab ? (
          <Typography
            variant="h6"
            color="info"
            mb={0}
            display="block"
            className="dragHandle"
            lineHeight="1rem"
          >
            Team {widgetTitle || displayName}
          </Typography>
        ) : (
          <Typography
            variant="h6"
            color="info"
            mb={0}
            display="block"
            className="dragHandle"
            lineHeight="1rem"
          >
            My {widgetTitle || displayName}
          </Typography>
        )}
        {widget.connectedStatus && periodDates.start && (
          <Box
            sx={{
              fontSize: "20px",
              display: "flex",
              alignItems: "center",
              mx: "-10px",
            }}
          >
            <ArrowLeft
              style={{ cursor: "pointer" }}
              onClick={() => handlePeriods("prev")}
              sx={{
                mr: "-3px",
              }}
            />
            <Typography variant="caption" color="info" mb={0} display="block">
              {formatDatesForWidget(timePeriod, periodDates)}
            </Typography>
            <ArrowRight
              style={{ cursor: "pointer" }}
              onClick={() => handlePeriods("next")}
              sx={{
                ml: "-3px",
              }}
            />
          </Box>
        )}
      </Box>

      {!!(widget.connectedStatus && !loading) && (
        <Grid
          container
          sx={{
            justifyContent: "space-between",
          }}
          wrap="nowrap"
        >
          <Grid item>
            <Grid
              container
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Grid item>
                <Typography
                  sx={{
                    fontSize: "3rem",
                    color: "#4daff4",
                  }}
                >
                  {displayData.underContract}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="info" mb={0} display="block">
                  Under Contract
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Grid item>
                <Typography
                  sx={{
                    fontSize: "3rem",
                    color: "#4daff4",
                  }}
                >
                  {displayData.closings}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="info" mb={0} display="block">
                  Closings
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Grid
              container
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Grid
                item
                sx={{
                  pb: "1.25rem",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    color: "#4daff4",
                  }}
                >
                  {displayData.avgPricePerClosing}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="info" mb={0} display="block">
                  Avg Price / Closing
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!widget.connectedStatus && (
        <Grid
          container
          sx={{
            justifyContent: "space-between",
            height: "100%",
          }}
          wrap="nowrap"
        >
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              height: "100%",
              alignItems: "end",
            }}
          >
            <LinkOffIcon fontSize="large" />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
